import { usePatient } from "./usePatient";
import { resolvePartnerCounterparty } from "../resolvePartnerCounterparty";

export type EffectiveGender = "male" | "female" | null;

/** Maps the API's AFAB/AMAB to "female"/"male" (or null). */
export function mapApiGender(
  apiGender: string | null | undefined,
): EffectiveGender {
  if (apiGender === "AFAB") return "female";
  if (apiGender === "AMAB") return "male";
  return null;
}

/**
 * Returns the user's effective gender plus their linked partner's gender.
 * Each person's gender is resolved independently so consumers can gate
 * content per-person (e.g., hide AFAB-only labs for an AMAB patient while
 * still showing them for an AFAB partner).
 */
export function useCarePersons() {
  const {
    data: self,
    isError: isSelfError,
    isLoading: isSelfLoading,
  } = usePatient();
  const partnerResolution = resolvePartnerCounterparty(self?.counterparties);
  const partnerId =
    partnerResolution.state === "resolved" ? partnerResolution.patientId : null;
  const { data: partner } = usePatient({ patientId: partnerId ?? null });

  const selfGender = mapApiGender(self?.gender);
  const partnerGender = mapApiGender(partner?.gender);

  const hasLinkedPartner = !!partnerId && !!partner;
  const partnerConnected = !!self?.partnerConnected;
  const partnerName = partner?.firstName ?? null;

  const partnerContextState = isSelfLoading
    ? "loading"
    : isSelfError
      ? "unavailable"
      : partnerResolution.state;

  return {
    selfGender,
    partnerGender,
    hasLinkedPartner,
    partnerConnected,
    partnerName,
    partnerPatient: partner,
    partnerId: partnerId ?? null,
    partnerContextState,
  };
}
